import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const SecuritySettings = ({ data, onUpdate }) => {
  const [localData, setLocalData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  const handleSave = () => {
    onUpdate(localData);
    setHasChanges(false);
  };

  const updateSecuritySetting = (setting, value) => {
    setLocalData(prev => ({
      ...prev,
      [setting]: value
    }));
    setHasChanges(true);
  };

  const handlePasswordChange = () => {
    const errors = {};
    
    // Basic validation
    if (!passwordData?.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    if (!passwordData?.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData?.newPassword?.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);

    if (Object.keys(errors)?.length === 0) {
      // API call to change password
      console.log('Password change requested');
      updateSecuritySetting('lastPasswordChange', new Date()?.toISOString());
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const handleEnable2FA = () => {
    // In a real app, this would show QR code setup
    updateSecuritySetting('twoFactorEnabled', true);
    updateSecuritySetting('twoFactorMethod', 'authenticator');
  };

  const handleDisable2FA = () => {
    if (window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      updateSecuritySetting('twoFactorEnabled', false);
      updateSecuritySetting('twoFactorMethod', null);
    }
  };

  const removeTrustedDevice = (deviceId) => {
    const updatedDevices = localData?.trustedDevices?.filter(device => device?.id !== deviceId);
    setLocalData(prev => ({
      ...prev,
      trustedDevices: updatedDevices
    }));
    setHasChanges(true);
  };

  const formatLoginDate = (dateString) => {
    return new Date(dateString)?.toLocaleString();
  };

  const getDeviceIcon = (deviceName) => {
    if (deviceName?.toLowerCase()?.includes('iphone') || deviceName?.toLowerCase()?.includes('safari')) {
      return 'Smartphone';
    } else if (deviceName?.toLowerCase()?.includes('chrome') || deviceName?.toLowerCase()?.includes('firefox')) {
      return 'Monitor';
    } else {
      return 'Laptop';
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Security Settings</h2>
          <p className="text-muted-foreground mt-1">
            Manage your account security and authentication preferences
          </p>
        </div>
        
        {hasChanges && (
          <Button
            onClick={handleSave}
            iconName="Save"
            size="sm"
          >
            Save Changes
          </Button>
        )}
      </div>
      {/* Password Management */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Key" size={20} className="mr-2" />
          Password Management
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="font-medium text-foreground">Password</p>
              <p className="text-sm text-muted-foreground">
                Last changed: {new Date(data?.lastPasswordChange)?.toLocaleDateString()}
              </p>
            </div>
            <Button
              onClick={() => setShowPasswordChange(!showPasswordChange)}
              variant="outline"
              size="sm"
            >
              Change Password
            </Button>
          </div>

          {showPasswordChange && (
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-foreground">Change Password</h4>
              
              <div className="space-y-4">
                <Input
                  label="Current Password"
                  type="password"
                  value={passwordData?.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e?.target?.value }))}
                  error={passwordErrors?.currentPassword}
                />
                
                <Input
                  label="New Password"
                  type="password"
                  value={passwordData?.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e?.target?.value }))}
                  error={passwordErrors?.newPassword}
                  description="Password must be at least 8 characters"
                />
                
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={passwordData?.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                  error={passwordErrors?.confirmPassword}
                />
              </div>
              
              <div className="flex space-x-2">
                <Button
                  onClick={handlePasswordChange}
                  size="sm"
                >
                  Update Password
                </Button>
                <Button
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordErrors({});
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Two-Factor Authentication */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Shield" size={20} className="mr-2" />
          Two-Factor Authentication
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                localData?.twoFactorEnabled ? 'bg-success' : 'bg-muted-foreground'
              }`} />
              <div>
                <p className="font-medium text-foreground">
                  Two-Factor Authentication
                </p>
                <p className="text-sm text-muted-foreground">
                  {localData?.twoFactorEnabled 
                    ? `Enabled via ${localData?.twoFactorMethod}` 
                    : 'Not enabled - recommended for security'
                  }
                </p>
              </div>
            </div>
            
            {localData?.twoFactorEnabled ? (
              <Button
                onClick={handleDisable2FA}
                variant="outline"
                size="sm"
              >
                Disable
              </Button>
            ) : (
              <Button
                onClick={handleEnable2FA}
                size="sm"
              >
                Enable 2FA
              </Button>
            )}
          </div>

          {localData?.twoFactorEnabled && (
            <div className="text-sm text-muted-foreground bg-success/10 p-4 rounded-lg border border-success/20">
              <div className="flex items-center space-x-2 text-success">
                <Icon name="CheckCircle" size={16} />
                <span className="font-medium">Two-factor authentication is active</span>
              </div>
              <p className="mt-2">Your account is protected with additional security.</p>
            </div>
          )}
        </div>
      </div>
      {/* Login History */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Clock" size={20} className="mr-2" />
          Recent Login Activity
        </h3>
        
        <div className="space-y-4">
          {localData?.loginHistory?.map((login) => (
            <div key={login?.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name={getDeviceIcon(login?.device)} size={20} className="text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">{login?.device}</p>
                  <p className="text-sm text-muted-foreground">
                    {login?.location} • {formatLoginDate(login?.date)}
                  </p>
                  <p className="text-xs text-muted-foreground">IP: {login?.ipAddress}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-success" />
                <span className="text-sm text-success">Successful</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Trusted Devices */}
      <div className="bg-card rounded-lg p-6 border">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
          <Icon name="Smartphone" size={20} className="mr-2" />
          Trusted Devices
        </h3>
        
        <div className="space-y-4">
          {localData?.trustedDevices?.map((device) => (
            <div key={device?.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Monitor" size={20} className="text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">{device?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Last used: {formatLoginDate(device?.lastUsed)}
                  </p>
                  <p className="text-xs text-muted-foreground">{device?.location}</p>
                </div>
              </div>
              
              <Button
                onClick={() => removeTrustedDevice(device?.id)}
                variant="ghost"
                size="sm"
                iconName="X"
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Security Recommendations */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Security Recommendations</h4>
            <div className="text-xs text-blue-800 space-y-1">
              <p>• Use a strong, unique password for your account</p>
              <p>• Enable two-factor authentication for enhanced security</p>
              <p>• Regularly review your login activity</p>
              <p>• Keep your trusted devices list up to date</p>
              <p>• Use secure networks when accessing your account</p>
              <p>• Log out from public or shared devices</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;